import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import ContentMasterMenuSection from './ContentMasterMenuSection';
import { useAccountFeatures } from '../../hooks/useLicenseCheck';
import { usePermissions } from '../../hooks/usePermissions';
import { RootState } from '../../store/store';
import {
  getActiveSidebarEntryId,
  getRouteExpandedIds,
  SIDEBAR_MENU,
  SidebarMenuEntry,
} from './sidebarMenuConfig';

const CONTENT_MASTER_ENTRY_ID = 'content-master';
const TASKS_ENTRY_ID = 'tasks';

interface SidebarNavProps {
  isSubuser: boolean;
  onNavigate?: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ isSubuser, onNavigate }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const activeEntryId = getActiveSidebarEntryId(pathname);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => getRouteExpandedIds(pathname));
  // Tasks (Listing Inspector) requires an active mobile Pro/Ultimate/Unlimited plan.
  const { showTasks } = useAccountFeatures();
  const { can } = usePermissions();
  // Partner entry: `UserView.isAffiliate` (profile / login / register payloads)
  // is true when the user's email matches an active affiliate partner.
  const isAffiliate = useSelector(
    (state: RootState) => !!state.profile.contact?.isAffiliate
  );

  const visibleMenu = useMemo(
    () =>
      SIDEBAR_MENU.filter((entry) => {
        if (isSubuser && entry.hiddenForSubuser) return false;
        if (entry.requiredPermission && !can(entry.requiredPermission)) return false;
        if (entry.id === TASKS_ENTRY_ID && !showTasks) return false;
        if (entry.partnerOnly && !isAffiliate) return false;
        return true;
      }),
    [isSubuser, showTasks, isAffiliate, can],
  );

  useEffect(() => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      getRouteExpandedIds(pathname).forEach((id) => next.add(id));
      return next;
    });
  }, [pathname]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const renderSubMenu = (children: SidebarMenuEntry[]) => (
    <ul className="nav-submenu">
      {children.map((child) => {
        const isActive = activeEntryId === child.id;
        const label = t(child.labelKey);
        const rowClassName = `nav-sub-item${isActive ? ' active' : ''}`;
        const rowContent = (
          <>
            <span className="nav-sub-item-icon">
              <img src={child.icon} alt="" aria-hidden="true" />
            </span>
            <span className="menu-item-label">{label}</span>
          </>
        );

        const rowInner = (
          <span className="nav-sub-item-row">
            {rowContent}
          </span>
        );

        return (
          <li key={child.id} className={rowClassName}>
            {child.to ? (
              <Link to={child.to} className="nav-sub-item-link" onClick={onNavigate}>
                {rowInner}
              </Link>
            ) : (
              <span className="nav-sub-item-link nav-sub-item-static" role="presentation">
                {rowInner}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderMenuEntry = (entry: SidebarMenuEntry) => {
    const isActive = activeEntryId === entry.id;
    const isExpanded = expandedIds.has(entry.id);
    const label = t(entry.labelKey);
    const hasChildren = Boolean(entry.children?.length);
    const showSubMenu = entry.isExpandable && isExpanded && hasChildren;

    const labelContent = (
      <>
        <span className="nav-item-icon">
          <img src={entry.icon} alt="" aria-hidden="true" />
        </span>
        <span className="menu-item-label">{label}</span>
      </>
    );

    const chevronButton = entry.isExpandable ? (
      <button
        type="button"
        className="nav-item-chevron-btn"
        onClick={() => toggleExpanded(entry.id)}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t('sidebar_collapse_section', { section: label }) : t('sidebar_expand_section', { section: label })}
      >
        <img src={isExpanded ? chevronUpIcon : chevronDownIcon} alt="" aria-hidden="true" />
      </button>
    ) : null;

    const rowClassName = [
      'nav-item',
      entry.isExpandable ? 'nav-item-group' : '',
      isActive ? 'active' : '',
    ]
      .filter(Boolean)
      .join(' ');

    let primaryControl: React.ReactNode;

    if (entry.to) {
      primaryControl = (
        <Link to={entry.to} className="nav-item-primary" onClick={onNavigate}>
          {labelContent}
        </Link>
      );
    } else if (entry.isExpandable) {
      primaryControl = (
        <button
          type="button"
          className="nav-item-primary"
          onClick={() => toggleExpanded(entry.id)}
        >
          {labelContent}
        </button>
      );
    } else {
      primaryControl = (
        <span className="nav-item-primary nav-item-primary-static" role="presentation">
          {labelContent}
        </span>
      );
    }

    return (
      <li key={entry.id} className={rowClassName}>
        <div className="nav-item-row">
          {primaryControl}
          {chevronButton}
        </div>
        {showSubMenu && entry.children && renderSubMenu(entry.children)}
      </li>
    );
  };

  return (
    <ul>
      {visibleMenu.map((entry) => {
        if (entry.id === CONTENT_MASTER_ENTRY_ID) {
          return <ContentMasterMenuSection key={entry.id} onNavigate={onNavigate} />;
        }
        return renderMenuEntry(entry);
      })}
    </ul>
  );
};

export default SidebarNav;
